<template>
  <div v-if="visible" class="loading-message">
    <transition name="loading-message">
      <div v-if="messageVisible" class="loading-message__message">
        <div class="loading-message__spinner">
          <div class="loading-message__spinner__dot1"></div>
          <div class="loading-message__spinner__dot2"></div>
          <div class="loading-message__spinner__dot3"></div>
        </div>
        <span>{{ $t("messages.loadingData") }}</span>
        <transition name="loading-message__progressbar">
          <div v-if="progressTracker && progressVisible">
            <progress
              class="loading-message__progressbar"
              max="100"
              :value="progress"
            />
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from "vue";

interface Props {
  visible?: boolean;
  progressTracker?: { [requestId: string]: { percent: number } };
}
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  progressTracker: undefined
});

const messageVisible = ref(false);
const progressVisible = ref(false);

const progress = computed(() => {
  if (!props.progressTracker) return 0;

  let percent = 0;
  let totalPercent = 0;
  for (const requestId in props.progressTracker) {
    const progressEvent = props.progressTracker[requestId];
    percent += progressEvent?.percent ?? 0; // number from 0 - 100
    totalPercent += 100;
  }

  if (totalPercent == 0) return 0;

  return Math.round((percent * 100) / totalPercent);
});

watch(
  () => props.visible,
  (value) => {
    if (value) {
      nextTick(() => {
        messageVisible.value = value;
      });
      setTimeout(() => {
        progressVisible.value = props.visible;
      }, 1000);
    } else {
      messageVisible.value = false;
      progressVisible.value = false;
    }
  }
);
</script>

<style lang="scss">
.loading-message {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-backdrop);
  z-index: 12;

  // Animations
  &-enter-active {
    transition: opacity 125ms, transform 125ms;
  }
  &-leave-active {
    transition: opacity 125ms, transform 125ms;
  }
  &-enter-from {
    opacity: 0;
    transform: translate3d(0, 50px, 0);
  }
  &-leave-to {
    opacity: 0;
    transform: translate3d(0, -50px, 0);
  }

  &__message {
    text-align: center;
    background-color: var(--color-background-deep);
    padding: 3rem;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1), 0 16px 40px rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    width: 32rem;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__progressbar {
    width: 200px;
    max-width: 100%;
    height: 10px;
    border-radius: 5px;

    -webkit-appearance: none;
    appearance: none;

    &::-webkit-progress-bar {
      background-color: var(--color-background);
      border-radius: 5px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1) inset;
    }
    &::-webkit-progress-value {
      border-radius: 5px;
      background-color: var(--color-action);
    }
    &::-moz-progress-bar {
      border-radius: 5px;
      background-color: var(--color-action);
    }

    &-enter-active {
      transition: opacity 2s;
    }
    &-enter-from {
      opacity: 0;
    }
  }

  &__spinner {
    margin: 0 auto;
    display: flex;
    gap: 0.5em;
    justify-content: center;

    &__dot1,
    &__dot2,
    &__dot3 {
      width: 12px;
      height: 12px;
      background-color: var(--color-action);

      border-radius: 100%;
      animation: loading-message-bouncedelay 1.4s infinite ease-in-out both;
    }

    &__dot1 {
      animation-delay: -320ms;
    }
    &__dot2 {
      animation-delay: -160ms;
    }
  }
}

@keyframes loading-message-bouncedelay {
  0%,
  80%,
  100% {
    transform: scale(0.75);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
